import QrCode from "@/components/base/QrCode";
import {
  ORDER_SOURCE_CONFIG,
  PAYMENT_CONFIG,
  PICKUP_CONFIG,
} from "@/constants/status";
import { storeProfile } from "@/mocks/store";
import type { TaxInvoice } from "@/types";
import { formatSAR } from "@/utils/format";
import { calcIncludedVat } from "@/utils/vat";
import { buildZatcaQrValue } from "@/utils/zatca";

interface TaxInvoiceViewProps {
  invoice: TaxInvoice;
}

export default function TaxInvoiceView({ invoice }: TaxInvoiceViewProps) {
  const payment = PAYMENT_CONFIG[invoice.payment];
  const pickup = invoice.pickup ? PICKUP_CONFIG[invoice.pickup] : null;
  const source = invoice.source ? ORDER_SOURCE_CONFIG[invoice.source] : null;
  // Total VAT included in the whole invoice (products + the fixed app fee).
  const vatTotal = calcIncludedVat(invoice.total);
  const qrValue = buildZatcaQrValue({
    sellerName: storeProfile.brandName,
    vatNumber: storeProfile.vatNumber,
    timestamp: invoice.timestamp,
    totalWithVat: invoice.total,
    vatTotal,
  });

  const typeLabel =
    invoice.invoiceType === "standard" ? "فاتورة ضريبية" : "فاتورة ضريبية مبسطة";

  return (
    <div className="bg-white text-foreground-950 p-8 md:p-10" dir="rtl">
      <div className="flex items-start justify-between gap-6 border-b-2 border-foreground-950 pb-6">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-white shrink-0">
            <i className="ri-store-3-line text-2xl"></i>
          </span>
          <div>
            <h2 className="text-xl font-bold text-foreground-950">
              {storeProfile.brandName}
            </h2>
            <p className="text-xs text-foreground-500 mt-0.5">
              {storeProfile.address}
            </p>
            <p className="text-xs text-foreground-500 mt-0.5" dir="ltr">
              {storeProfile.phone}
            </p>
          </div>
        </div>
        <div className="text-left shrink-0">
          <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-800">
            {typeLabel}
          </span>
          <p className="text-lg font-bold text-foreground-950 mt-2" dir="ltr">
            {invoice.id}
          </p>
          <p className="text-xs text-foreground-500 mt-1">{invoice.date}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-5 text-sm">
        <div className="sm:col-span-2 space-y-2">
          <div>
            <p className="text-xs text-foreground-500">السجل التجاري</p>
            <p className="font-medium">{storeProfile.commercialRegister}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">
              الرقم الضريبي للمنشأة
            </p>
            <p className="font-medium" dir="ltr">
              {storeProfile.vatNumber}
            </p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">الفرع</p>
            <p className="font-medium">{invoice.branch}</p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center rounded-box border border-background-200 bg-background-50 p-3">
          <QrCode value={qrValue} size={120} alt="رمز QR للفاتورة الضريبية (ZATCA)" />
          <p className="mt-2 text-center text-[11px] text-foreground-500 leading-5">
            رمز الاستجابة السريعة
            <br />
            (متوافق مع ZATCA)
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-foreground-200 p-4 text-sm">
        <p className="text-xs font-semibold text-foreground-500 mb-2">
          فاتورة إلى
        </p>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-1">
          <span className="font-bold">{invoice.customer}</span>
          <span dir="ltr" className="font-medium text-foreground-700">
            {invoice.phone}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto mt-5">
      <table className="w-full min-w-[460px] text-sm border-collapse">
        <thead>
          <tr className="border-b border-foreground-200 text-xs text-foreground-500">
            <th className="text-right font-medium py-2 w-10">#</th>
            <th className="text-right font-medium py-2">المنتج</th>
            <th className="text-right font-medium py-2">الكمية</th>
            <th className="text-right font-medium py-2">سعر الوحدة</th>
            <th className="text-left font-medium py-2">الإجمالي</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line, index) => (
            <tr
              key={`${line.name}-${index}`}
              className="border-b border-foreground-100"
            >
              <td className="py-3 text-foreground-500">{index + 1}</td>
              <td className="py-3 font-medium">{line.name}</td>
              <td className="py-3">{line.quantity}</td>
              <td className="py-3">{formatSAR(line.price)}</td>
              <td className="py-3 text-left font-semibold">
                {formatSAR(line.quantity * line.price)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div className="flex justify-end mt-5">
        <div className="w-full max-w-xs space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-foreground-500">
              إجمالي المنتجات شامل الضريبة
            </span>
            <span className="font-medium">{formatSAR(invoice.subtotal)}</span>
          </div>
          {invoice.discount > 0 && (
            <div className="flex justify-between">
              <span className="text-foreground-500">الخصم</span>
              <span className="font-medium text-red-600">
                - {formatSAR(invoice.discount)}
              </span>
            </div>
          )}
          {!!invoice.deliveryFee && invoice.deliveryFee > 0 && (
            <div className="flex justify-between">
              <span className="text-foreground-500">رسوم التوصيل</span>
              <span className="font-medium">
                {formatSAR(invoice.deliveryFee)}
              </span>
            </div>
          )}
          {!!invoice.appFee && invoice.appFee > 0 && (
            <div className="flex justify-between">
              <span className="text-foreground-500">
                رسوم التطبيق شاملة الضريبة
              </span>
              <span className="font-medium">{formatSAR(invoice.appFee)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-foreground-500">
              الضريبة المتضمنة (15%)
            </span>
            <span className="font-medium">{formatSAR(vatTotal)}</span>
          </div>
          <div className="flex justify-between border-t-2 border-foreground-950 pt-2 text-base">
            <span className="font-bold">الإجمالي (شامل الضريبة)</span>
            <span className="font-bold">{formatSAR(invoice.total)}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-sm border-t border-foreground-200 pt-4">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
            <i className={payment.icon}></i>
          </span>
          <span className="text-foreground-500">طريقة الدفع:</span>
          <span className="font-medium">{payment.label}</span>
        </div>
        {pickup && (
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
              <i className={pickup.icon}></i>
            </span>
            <span className="text-foreground-500">طريقة الاستلام:</span>
            <span className="font-medium">{pickup.label}</span>
          </div>
        )}
        {source && (
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
              <i className={source.icon}></i>
            </span>
            <span className="text-foreground-500">مصدر الطلب:</span>
            <span className="font-medium">{source.label}</span>
          </div>
        )}
      </div>

      <p className="mt-8 text-center text-xs text-foreground-400 border-t border-foreground-100 pt-4">
        شكرًا لتعاملكم مع {storeProfile.brandName} — هذه فاتورة ضريبية إلكترونية
        صادرة وفقًا لمتطلبات هيئة الزكاة والضريبة والجمارك
      </p>
    </div>
  );
}